<script>
  import { messageThumbnails } from '../message-images.js';
  import { afterUpdate, createEventDispatcher } from 'svelte';
  import { longpress } from '../actions/longpress.js';
  import { isKeyboardVisible } from '../viewport.js';
  import { lazyImage } from '../actions/lazy-image.js';
  import { renderMarkdown } from '../markdown.js';
  import { TEXT, fallbackLabel, stopReasonLabel } from '../ui-text.js';
  import { ICONS } from '../icons.js';
  
  export let message;
  export let isStreaming = false;
  export let showSystemMessages = false;
  // Статус доставки user-сообщения: 'queued' | 'sending' | 'sent' | 'failed' | null (delivery.js).
  export let deliveryState = null;
  // Автор ЧУЖОГО user-сообщения в общем чате: {name, avatarDataUrl} | null.
  // Со значением пузырь уходит влево и получает подпись с аватаркой.
  export let author = null;
  // «Прочитано всеми» (общий чат): галочки у своего сообщения.
  export let readByAll = false;
  // Быстрые действия под последним сообщением ленты (1.27.0): список из
  // 'copy' | 'regenerate' | 'share' под ответом или ['retry'] под вопросом без ответа.
  export let quickActions = null;

  const QUICK_ACTIONS = {
    copy: { icon: 'copy', label: TEXT.chat.copyAction, aria: TEXT.chat.copyAnswerAria },
    regenerate: { icon: 'repeat', label: TEXT.chat.regenerateAction, aria: TEXT.chat.regenerateAria },
    share: { icon: 'share', label: TEXT.chat.shareAction, aria: TEXT.chat.shareAnswerAria },
    retry: { icon: 'repeat', label: TEXT.chat.retryAction, aria: TEXT.chat.retryAria, withLabel: true },
  };

  // «Отправляется…» показываем только если ожидание затянулось (>1.5с) — на живой
  // сети ACK приходит быстро и индикатор не мигает.
  let showSending = false;
  let sendingTimer = null;
  $: {
    if (deliveryState === 'sending') {
      if (!sendingTimer && !showSending) {
        sendingTimer = setTimeout(() => {
          sendingTimer = null;
          showSending = true;
        }, 1500);
      }
    } else {
      if (sendingTimer) {
        clearTimeout(sendingTimer);
        sendingTimer = null;
      }
      showSending = false;
    }
  }

  const dispatch = createEventDispatcher();
  
  $: isUser = message.role === 'user';
  $: isSystem = message.role === 'system';
  // Этап 22: хвост, отброшенный правкой, остаётся в ленте, но Петрович его не видит.
  $: isExcluded = Boolean(message.excludedFromContext);
  $: isForeign = isUser && Boolean(author);
  // Подпись автора: у чужого user-сообщения (foreign) и у ответа Петровича в
  // общем чате (assistant с author) — он там равноправный участник.
  $: showAuthor = Boolean(author) && (isUser || (!isUser && !isSystem));
  $: renderedContent = isUser ? message.text : renderMarkdown(message.text);
  // Этап 32: в ленте — миниатюры (рамка 4:3 до 280 px высотой; оригинал не нужен).
  $: images = messageThumbnails(message);
  $: hasImage = images.length > 0;
  $: hasText = Boolean(message.text);
  $: imageAlt = isUser ? 'Прикреплённое изображение пользователя' : 'Изображение от ассистента';
  // 1.27.0: недописанный ответ — остановлен человеком или оборван ошибкой.
  $: stopBadge = !isUser && !isSystem && stopReasonLabel(message.stopReason)
    ? {
      label: stopReasonLabel(message.stopReason),
      title: message.stopReason === 'error' ? TEXT.chat.interruptedTitle : TEXT.chat.stoppedTitle,
    }
    : null;
  $: visibleQuickActions = (quickActions || []).filter((id) => QUICK_ACTIONS[id]);
  
  function formatTime(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }

  function triggerAction(type) {
    dispatch('action', { type, message });
  }

  // Тап по бабблу открывает шторку действий; ссылки, картинки и кнопки внутри
  // (копирование код-блока) — исключение; стриминговый баббл действий не имеет.
  // При открытой клавиатуре первый тап только убирает её: баббл ответа занимает
  // почти весь экран, и попытка «нажать на свободное место» иначе каждый раз
  // открывала шторку (жалоба владельца 2026-09-12).
  // Состояние клавиатуры снимаем на pointerdown: к моменту click она уже уезжает.
  let keyboardWasOpenOnPress = false;

  function handleBubblePointerDown() {
    keyboardWasOpenOnPress = isKeyboardVisible();
  }

  function handleBubbleClick(event) {
    if (isStreaming || event.target.closest('a, img, button')) {
      return;
    }
    if (keyboardWasOpenOnPress) {
      keyboardWasOpenOnPress = false;
      const active = document.activeElement;
      if (active && typeof active.blur === 'function') {
        active.blur();
      }
      return;
    }
    triggerAction('open-actions');
  }

  // Кнопка «Копировать» на код-блоках ответа: {@html} пересоздаёт DOM при каждом
  // обновлении контента, поэтому довешиваем кнопки после каждого рендера.
  let contentElement = null;

  afterUpdate(() => {
    if (!contentElement) {
      return;
    }
    for (const preElement of contentElement.querySelectorAll('pre:not([data-copy-ready])')) {
      preElement.dataset.copyReady = '1';
      const copyButton = document.createElement('button');
      copyButton.type = 'button';
      copyButton.className = 'code-copy';
      copyButton.textContent = 'Копировать';
      copyButton.addEventListener('click', async (event) => {
        event.stopPropagation();
        // Кнопка — сиблинг <code> внутри <pre>: innerText самого code её не содержит.
        const codeText = preElement.querySelector('code')?.innerText ?? '';
        try {
          await navigator.clipboard.writeText(codeText);
          copyButton.textContent = 'Скопировано';
          setTimeout(() => {
            copyButton.textContent = 'Копировать';
          }, 1500);
        } catch (_) {
          // Буфер обмена недоступен (не-secure context) — кнопка просто не сработала.
        }
      });
      preElement.appendChild(copyButton);
    }
  });
</script>

{#if isSystem && !showSystemMessages}
  <!-- Hidden system message -->
{:else}
  <div class="message" class:user={isUser && !isForeign} class:foreign={isForeign} class:assistant={!isUser && !isSystem} class:system={isSystem} class:excluded={isExcluded}>
    {#if showAuthor}
      <div class="message-author">
        {#if author.avatarDataUrl}
          <img class="author-avatar" src={author.avatarDataUrl} alt="" />
        {:else}
          <span class="author-avatar author-avatar-fallback">{(author.name || '?').slice(0, 1).toUpperCase()}</span>
        {/if}
        <span class="author-name">{author.name}</span>
      </div>
    {/if}
    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
    <div
      class="message-bubble"
      class:streaming={isStreaming}
      use:longpress
      on:longpress={() => triggerAction('open-actions')}
      on:pointerdown={handleBubblePointerDown}
      on:click={handleBubbleClick}
    >
      {#if isUser}
        {#if hasImage}
          <div class="message-gallery" class:single={images.length === 1}>
            {#each images as image, i (i)}
              <img class="message-image" alt={`Прикреплённое изображение пользователя ${i + 1}`} decoding="async" use:lazyImage={{ src: image }} />
            {/each}
          </div>
        {/if}
        {#if hasText}
          <p class="message-text">{message.text}</p>
        {/if}
      {:else if isSystem}
        <p class="system-label">Системный промпт</p>
        <p class="message-text">{message.text}</p>
      {:else}
        {#if hasImage}
          <div class="message-gallery" class:single={images.length === 1}>
            {#each images as image, i (i)}
              <img class="message-image" alt={imageAlt} decoding="async" use:lazyImage={{ src: image }} />
            {/each}
          </div>
        {/if}
        <div class="message-content markdown-body" bind:this={contentElement}>
          {@html renderedContent}
        </div>
      {/if}
      
      {#if isStreaming}
        <span class="cursor">▋</span>
      {/if}
    </div>

    {#if isUser && deliveryState === 'photo-lost'}
      <!-- К5 (1.27.7): картинки очереди не пережили перезагрузку — повторять нечего. -->
      <div class="delivery-status failed">
        <span>⚠︎ {TEXT.chat.photoLost}</span>
        <button
          type="button"
          class="delivery-retry"
          aria-label={TEXT.chat.reattachAria}
          on:click={() => dispatch('action', { type: 'reattach', message })}
        >
          {TEXT.chat.reattach}
        </button>
      </div>
    {:else if isUser && deliveryState === 'failed'}
      <div class="delivery-status failed">
        <span>⚠︎ Не доставлено</span>
        <button type="button" class="delivery-retry" on:click={() => dispatch('action', { type: 'retry-delivery', message })}>
          Повторить отправку
        </button>
      </div>
    {:else if isUser && deliveryState === 'queued'}
      <!-- Офлайн-очередь: без задержки (в отличие от «Отправляется…») — пользователь
           должен сразу видеть, что сообщение ждёт связи, а не ушло. -->
      <div class="delivery-status queued">Отправим при появлении связи</div>
    {:else if isUser && showSending}
      <div class="delivery-status sending">Отправляется…</div>
    {/if}

    <div class="message-meta">
      {#if message.ts}
        <span class="message-time">{formatTime(message.ts)}</span>
      {/if}
      {#if isExcluded}
        <span class="excluded-badge">{TEXT.edit.excludedBadge}</span>
      {/if}
      {#if stopBadge}
        <span class="stop-badge" title={stopBadge.title}>{stopBadge.label}</span>
      {/if}
      {#if message.role === 'assistant' && message.fallbackFrom}
        <!-- Этап 25.2: ответило резервное звено цепочки провайдеров. -->
        <span class="fallback-badge" title={`${TEXT.brain.fallbackInstead} ${message.fallbackFrom}`}>{fallbackLabel(message.model)}</span>
      {/if}
      {#if isUser && !isForeign && readByAll}
        <span class="read-mark" title={TEXT.family.readByAll} aria-label={TEXT.family.readByAll}>✓✓</span>
      {/if}
    </div>

    {#if visibleQuickActions.length}
      <div class="quick-actions" class:question={isUser} role="group" aria-label={TEXT.chat.quickActionsAria}>
        {#each visibleQuickActions as id (id)}
          <button
            type="button"
            class="quick-action"
            class:with-label={QUICK_ACTIONS[id].withLabel}
            aria-label={QUICK_ACTIONS[id].aria}
            title={QUICK_ACTIONS[id].label}
            on:click={() => triggerAction(id)}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d={ICONS[QUICK_ACTIONS[id].icon]} />
            </svg>
            {#if QUICK_ACTIONS[id].withLabel}
              <span>{QUICK_ACTIONS[id].label}</span>
            {/if}
          </button>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style>
  .message {
    display: flex;
    flex-direction: column;
    margin-bottom: var(--spacing-md);
    width: 100%;
  }

  .message.user {
    align-self: flex-end;
    align-items: flex-end;
  }

  .message.assistant,
  .message.system,
  .message.foreign {
    align-self: flex-start;
    align-items: flex-start;
  }

  .message-author {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 0.25rem;
    padding: 0 0.25rem;
  }

  .author-avatar {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
    background: var(--bg-tertiary);
  }

  .author-avatar-fallback {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 600;
    color: var(--text-secondary);
    border: 1px solid var(--border-color);
  }

  .author-name {
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .message-bubble {
    padding: 0.75rem 1rem;
    border-radius: 16px;
    line-height: 1.5;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
    /* Long-press открывает шторку действий: системный callout iOS и выделение
       текста на touch конфликтуют с жестом — копирование живёт в шторке. */
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }

  @media (hover: hover) {
    /* На десктопе выделение текста мышью остаётся (шторка — по клику). */
    .message-bubble {
      -webkit-user-select: text;
      user-select: text;
    }
  }

  .user .message-bubble {
    background: var(--user-bg);
    color: var(--user-text);
    border-bottom-right-radius: 4px;
    width: fit-content;
    max-width: 85%;
  }

  .assistant .message-bubble {
    background: var(--assistant-bg);
    color: var(--assistant-text);
    border-bottom-left-radius: 4px;
    width: fit-content;
    max-width: 95%;
  }

  .foreign .message-bubble {
    background: var(--assistant-bg);
    color: var(--assistant-text);
    border-bottom-left-radius: 4px;
    width: fit-content;
    max-width: 85%;
  }

  .system .message-bubble {
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    border: 1px dashed var(--border-color);
    font-size: 0.875rem;
    width: 95%;
    max-width: 95%;
  }

  .assistant .message-content,
  .system .message-content {
    width: 100%;
  }

  .assistant .message-meta,
  .system .message-meta {
    width: 95%;
  }

  .system-label {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .message-text {
    white-space: pre-wrap;
  }

  .message-gallery {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.4rem;
    margin-bottom: 0.75rem;
    max-width: 280px;
  }

  .message-gallery.single {
    grid-template-columns: minmax(0, 1fr);
  }

  .message-image {
    display: block;
    width: 100%;
    /* aspect-ratio + фон: высота известна ДО декодирования — картинки, дорисованные
       reconcile'ом после гидрации из снапшота (он их не хранит), не прыгают. */
    aspect-ratio: 4 / 3;
    max-height: 280px;
    object-fit: cover;
    border-radius: 12px;
    border: 1px solid var(--border-light);
    background: var(--bg-tertiary);
  }

  .message-content :global(p) {
    margin-bottom: 0.75rem;
  }

  .message-content :global(p:last-child) {
    margin-bottom: 0;
  }

  .message-content :global(pre) {
    margin: 0.75rem 0;
    max-width: 100%;
    position: relative;
    padding-top: 2.1rem;
  }

  .message-content :global(.code-copy) {
    position: absolute;
    top: 0.4rem;
    right: 0.4rem;
    padding: 0.2rem 0.6rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    font-size: 0.75rem;
    font-family: var(--font-sans);
  }

  .message-content :global(.table-wrap) {
    overflow-x: auto;
    margin: 0.75rem 0;
    max-width: 100%;
  }

  .message-content :global(table) {
    border-collapse: collapse;
    font-size: 0.875rem;
    min-width: 100%;
  }

  .message-content :global(th),
  .message-content :global(td) {
    border: 1px solid var(--border-color);
    padding: 0.4rem 0.6rem;
    text-align: left;
    vertical-align: top;
  }

  .message-content :global(th) {
    background: var(--bg-tertiary);
    font-weight: 600;
  }

  .message-content :global(code) {
    /* Токен вместо rgba(0,0,0,.3): в светлой/сепия-теме чёрная плашка выглядела
       грязным пятном посреди светлого баббла. */
    background: var(--bg-tertiary);
  }

  .message-content :global(ul),
  .message-content :global(ol) {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  .message-content :global(li) {
    margin-bottom: 0.25rem;
  }

  .message-content :global(blockquote) {
    border-left: 3px solid var(--accent-color);
    margin: 0.75rem 0;
    padding-left: 1rem;
    color: var(--text-secondary);
  }

  .message-content :global(a) {
    color: var(--accent-hover);
  }

  .message-time {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .fallback-badge {
    font-size: 0.75rem;
    color: var(--warning-text);
  }

  .stop-badge {
    font-size: 0.75rem;
    color: var(--text-secondary);
    border: 1px solid var(--border-light);
    border-radius: 6px;
    padding: 0 0.3rem;
  }

  /* Быстрые действия (1.27.0): зона нажатия 44×44 при скромном значке —
     ряд не спорит с текстом ответа, но попадается большим пальцем. */
  .quick-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.1rem;
  }

  .quick-actions.question {
    justify-content: flex-end;
  }

  .quick-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    min-width: 44px;
    min-height: 44px;
    padding: 0 0.5rem;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: var(--text-secondary);
    font: inherit;
    font-size: 0.875rem;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .quick-action.with-label {
    padding: 0 0.85rem;
    border: 1px solid var(--border-color);
  }

  .quick-action:active {
    background: var(--bg-tertiary);
  }

  @media (hover: hover) {
    .quick-action:hover {
      background: var(--bg-tertiary);
      color: var(--text-primary);
    }
  }

  /* Отброшенный правкой хвост читается, но видно, что он вне разговора. */
  .message.excluded .message-bubble {
    opacity: 0.55;
    border: 1px dashed var(--border-color);
  }

  .excluded-badge {
    font-size: 0.7rem;
    color: var(--text-muted);
    border: 1px solid var(--border-light);
    border-radius: 6px;
    padding: 0 0.3rem;
  }

  .read-mark {
    font-size: 0.75rem;
    color: var(--accent-color);
    letter-spacing: -0.15em;
  }

  .delivery-status {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .delivery-status.failed {
    color: var(--error-text);
  }

  /* В11 (1.27.2): зона касания мелких кнопок добрана невидимым ::after до ~44px. */
  .delivery-retry::after,
  .message-content :global(.code-copy)::after {
    content: '';
    position: absolute;
    inset: -10px;
  }

  .delivery-retry {
    position: relative;
    padding: 0.15rem 0.6rem;
    border: 1px solid currentColor;
    border-radius: 8px;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: 0.75rem;
    cursor: pointer;
  }

  .message-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.25rem;
    padding: 0 0.25rem;
    flex-wrap: wrap;
  }

  .streaming .cursor {
    animation: blink 1s infinite;
    color: var(--accent-color);
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
</style>
